import {
  changeCartItemsCount,
  switchCheckoutUrlOnRerender,
} from '../services/cart';

import { fetchRebuyRecommendedProducts } from '../services/products';
import { waitObjectProperty } from '../utils/performance';
import {
  attachProductItemEvents,
  getProductItemBody,
  renderVideos,
} from './product-item';

const init = () => (
  cacheElements(),
  attachListeners(),
  renderRecommendedProducts()
);

const attachListeners = () => {
  document.addEventListener('cart:change', onCartChange);
  state.elements.cartDrawer.addEventListener('click', loopCheckoutRedirect);
};

const state = {
  elements: {},
  renderedRecommendedProducts: {},
  cartItemIds: [],
  recommendedProducts: [],
};

const cacheElements = () => {
  const cartDrawer = document.querySelector('cart-drawer');
  state.elements = {
    ...state.elements,
    cartItems: cartDrawer.querySelectorAll('tr.cart-item'),
    cartItemsContainer: cartDrawer.querySelector('.cart-items tbody'),
    cartDrawer,
    recommendedProductsContainers: document.querySelectorAll(
      '.drawer__recommended'
    ),
    recommendedProductsBodys: document.querySelectorAll(
      '.drawer__recommended .body'
    ),
    itemsContainer: document.querySelector('cart-drawer-items'),
  };
};

const onCartChange = (e) => {
  const { detail } = e;
  const quantity = detail?.parsedState?.item_count;
  if (quantity != undefined) {
    changeCartItemsCount(quantity, quantity);
  }
  switchCheckoutUrlOnRerender();
};

const loaderCSS = `
.lds-ring {
  display: inline-block;
  position: absolute;
  width: 80px;
  height: 80px;
  top: calc(50% + 0.5rem);
  left: 50%;
  transform: translate(-50%, -50%) scale(0.35);
  z-index: 1;
}
.lds-ring div {
  box-sizing: border-box;
  display: block;
  position: absolute;
  width: 64px;
  height: 64px;
  margin: 8px;
  border: 8px solid #fff;
  border-radius: 50%;
  animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  border-color: #fff transparent transparent transparent;
}
.lds-ring div:nth-child(1) {
  animation-delay: -0.45s;
}
.lds-ring div:nth-child(2) {
  animation-delay: -0.3s;
}
.lds-ring div:nth-child(3) {
  animation-delay: -0.15s;
}
@keyframes lds-ring {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
`;

const loaderHTML = `<div class="lds-ring"><div></div><div></div><div></div><div></div></div> <style>${loaderCSS}</style>`;

const loopCheckoutRedirect = (e) => {
  if (e.target.id !== 'CartDrawer-Checkout') return;
  e.preventDefault();
  const loopApp = window.LoopOnstore;
  const isLoopActive = loopApp?.isActive();
  if (isLoopActive) {
    const cartCTAs = e.target.closest('.cart__ctas');
    cartCTAs.classList.add('loading');
    cartCTAs.insertAdjacentHTML('afterbegin', loaderHTML);
    loopApp.submit();
  } else {
    window.location.href = e.target.getAttribute('href');
  }
};

const getCartItemIds = async () => {
  try {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error('Failed to fetch cart data');
    }
    const data = await response.json();
    const cartItemIds = data.items.map((item) => item.id);

    return {
      cartItemIds,
      cartItemsError: null,
    };
  } catch (error) {
    return {
      cartItemIds: [],
      cartItemsError: error.message,
    };
  }
};

const loadRecommendedProducts = async () => {
  const result = {
    products: [],
    productsError: null,
  };

  try {
    const { cartItemIds, cartItemsError } = await getCartItemIds();
    const isSameIds = cartItemIds.every((id) => state.cartItemIds.includes(id));
    if (!cartItemIds?.length) {
      result.products = [];
      state.recommendedProducts = [];
    } else if (isSameIds || cartItemsError) {
      result.products = state.recommendedProducts;
      result.productsError = cartItemsError;
    } else {
      const products = await fetchRebuyRecommendedProducts(cartItemIds, 10);
      state.cartItemIds = cartItemIds;
      state.recommendedProducts = products;
      state.renderedRecommendedProducts = {};
      result.products = products;
    }
  } catch (error) {
    result.productsError = error;
  }

  return result;
};

const handleNoRecommendedProducts = () => {
  state.elements.recommendedProductsContainers?.forEach((container) =>
    container.classList.remove('loaded')
  );
};

const renderProductCards = async (products) => {
  cacheElements();
  if (!state.elements.recommendedProductsBodys.length) return;
  if (GLBE_PARAMS.currencyCode !== 'USD') {
    await waitObjectProperty('PriceInfo', window.GLBE_PARAMS);
  }
  const productCardTemplates = products.map((product, index) => {
    const newProduct = { ...product };
    if (GLBE_PARAMS.currencyCode !== 'USD') {
      if (newProduct.compareAtPrice) {
        newProduct.compareAtPrice = glbeApp.modules.priceInfo.convertPrice(
          newProduct.compareAtPrice
        );
      }
      newProduct.price = glbeApp.modules.priceInfo.convertPrice(product.price);
    }
    return getProductItemBody(
      newProduct,
      index,
      state.renderedRecommendedProducts,
      false,
      state.elements.cartDrawer
    );
  });

  state.elements.recommendedProductsBodys.forEach((container) => {
    container.innerHTML = '';
    container.insertAdjacentHTML('beforeend', productCardTemplates.join(' '));
  });

  state.elements.recommendedProductsContainers.forEach((container) =>
    container.classList.add('loaded')
  );
  attachProductItemEvents(
    state.renderedRecommendedProducts,
    state.elements.cartDrawer,
    true
  );
  await renderVideos(products, state.renderedRecommendedProducts);
};

export const renderRecommendedProducts = async () => {
  const { products, productsError } = await loadRecommendedProducts();
  if (!products?.length || productsError) {
    handleNoRecommendedProducts();
    return;
  }
  await renderProductCards(products);
};

export default init;